import { 
  sendPasswordResetEmail, 
  verifyPasswordResetCode, 
  confirmPasswordReset,
  ActionCodeSettings
} from 'firebase/auth';
import { auth } from './firebaseConfig';

/**
 * Password Reset Service
 * Handles Firebase authentication password reset operations
 */

/**
 * Sends a password reset email to the specified email address
 * @param email - User's email address
 * @returns Promise that resolves when email is sent (or silently succeeds if email doesn't exist)
 * @throws Error if there's a network issue or invalid email format
 */
export const requestPasswordReset = async (email: string): Promise<void> => {
  try {
    // Configure action code settings for custom handler URL
    const actionCodeSettings: ActionCodeSettings = {
      // Use the current domain for the reset handler
      url: `${window.location.origin}/reset-password`,
      handleCodeInApp: true,
    };

    console.log('📧 Sending password reset email to:', email);
    
    // Send password reset email
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
    
    console.log('✅ Password reset email sent successfully');
    
    // Always return success to avoid user enumeration
    // Firebase will silently succeed even if email doesn't exist
  } catch (error: any) {
    console.error('❌ Error sending password reset email:', error);
    
    // Handle specific error codes
    if (error.code === 'auth/invalid-email') {
      throw new Error('INVALID_EMAIL');
    } else if (error.code === 'auth/user-not-found') {
      // Silently succeed to avoid user enumeration
      console.log('ℹ️ User not found, but returning success for security');
      return;
    } else if (error.code === 'auth/too-many-requests') {
      throw new Error('TOO_MANY_REQUESTS');
    } else {
      throw new Error('GENERIC_ERROR');
    }
  }
};

/**
 * Verifies a password reset code from the email link
 * @param oobCode - The action code from the password reset email URL
 * @returns Promise that resolves with the user's email if code is valid
 * @throws Error if code is invalid or expired
 */
export const verifyResetCode = async (oobCode: string): Promise<string> => {
  try {
    console.log('🔍 Verifying password reset code...');
    
    // Verify the password reset code
    const email = await verifyPasswordResetCode(auth, oobCode);
    
    console.log('✅ Password reset code verified for:', email);
    return email;
  } catch (error: any) {
    console.error('❌ Error verifying reset code:', error);
    
    // Handle specific error codes
    if (error.code === 'auth/invalid-action-code') {
      throw new Error('INVALID_CODE');
    } else if (error.code === 'auth/expired-action-code') {
      throw new Error('EXPIRED_CODE');
    } else {
      throw new Error('GENERIC_ERROR');
    }
  }
};

/**
 * Completes the password reset with a new password
 * @param oobCode - The action code from the password reset email URL
 * @param newPassword - The new password to set
 * @returns Promise that resolves when password is successfully reset
 * @throws Error if code is invalid, expired, or password is weak
 */
export const completePasswordReset = async (
  oobCode: string, 
  newPassword: string
): Promise<void> => {
  try {
    console.log('🔐 Completing password reset...');
    
    // Validate password strength (minimum 8 characters)
    if (newPassword.length < 6) {
      throw new Error('WEAK_PASSWORD');
    }
    
    // Confirm the password reset
    await confirmPasswordReset(auth, oobCode, newPassword);
    
    console.log('✅ Password reset completed successfully');
  } catch (error: any) {
    console.error('❌ Error completing password reset:', error);
    
    // Handle specific error codes
    if (error.message === 'WEAK_PASSWORD' || error.code === 'auth/weak-password') {
      throw new Error('WEAK_PASSWORD');
    } else if (error.code === 'auth/invalid-action-code') {
      throw new Error('INVALID_CODE');
    } else if (error.code === 'auth/expired-action-code') {
      throw new Error('EXPIRED_CODE');
    } else {
      throw new Error('GENERIC_ERROR');
    }
  }
};

/**
 * Validates email format
 * @param email - Email address to validate
 * @returns boolean indicating if email is valid
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates password strength
 * @param password - Password to validate
 * @returns boolean indicating if password meets minimum requirements
 */
export const isValidPassword = (password: string): boolean => {
  return password.length >= 6;
};

